import { observer } from "mobx-react";
import "./rma-list-filters-sidebar.scss";
import { Button } from "../../../../../components/inputs/button/button";
import { BUTTON_VARIANTS } from "../../../../../components/inputs/button-variants";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import {
    MANAGE_RETURNS_DESIGN_VERSION,
    trackManageReturnsListEvent,
} from "../../../utils/manage-returns-list-mixpanel";
import { FEATURES } from "../../../../../models/Plan";
import currentPortalStore from "../../../../../stores/current-portal-store";
import RmaListFiltersStore from "../../../rma-list-page/stores/rma-list-filters-store";
import rmaPagedListStore from "../../stores/rma-tabs-paged-list-store";
import { RmaListFilterPopoverGroup } from "./components/rma-list-filter-popover-group/rma-list-filter-popover-group";
import RmaListFilterDateRange from "./components/rma-list-filter-date-range/rma-list-filter-date-range";
import RmaListFilterReservedItem from "./components/rma-list-filter-reserved-item/rma-list-filter-reserved-item";
import RmaListFilterResolution from "./components/rma-list-filter-resolution/rma-list-filter-resolution";
import RmaListFilterReturnReason from "./components/rma-list-filter-return-reason/rma-list-filter-return-reason";
import RmaListFilterValidated from "./components/rma-list-filter-validated/rma-list-filter-validated";
import RmaListQuickFiltersTabs from "./components/rma-list-quick-filters-tabs/rma-list-quick-filters-tabs";
import RmaListActionsStatusFilter, { RMA_BUCKET_KIND } from "./components/rma-list-actions-status-filter/rma-list-actions-status-filter";
import RmaListSelectedFilters from "./components/rma-list-selected-filters/rma-list-selected-filters";
import RmaListFiltersSidebarClearAll from "./components/rma-list-filters-sidebar-clear-all/rma-list-filters-sidebar-clear-all";
import RmaListFiltersSidebarTitle from "./components/rma-list-filters-sidebar-title/rma-list-filters-sidebar-title";

interface IProps {
    rmaListFiltersStore: RmaListFiltersStore;
    disabled: boolean;
    onFiltersApplied?: () => void;
}

/** Mixpanel event per bucket; a map so a new bucket cannot silently reuse a sibling's event. */
const BUCKET_APPLY_EVENT: Record<RMA_BUCKET_KIND, string> = {
    [RMA_BUCKET_KIND.MANUAL_ACTIONS]: "d&_manual_actions_apply",
    [RMA_BUCKET_KIND.ERRORS]: "d&_errors_apply",
    [RMA_BUCKET_KIND.ON_TRACK]: "d&_on_track_apply",
};

const RmaListFiltersSidebar: React.FC<IProps> = observer(
    ({ rmaListFiltersStore, disabled, onFiltersApplied }) => {
        const { getTranslation } = useDashboardTranslation();
        const scope = "dashboard-returns-page";
        const draftCount = rmaListFiltersStore.draftWiredSidebarFilterCount;
        const isReserveItemsAllowed = currentPortalStore.isFeatureAllowed(FEATURES.RESERVE_ITEMS);
        const applyLabel =
            draftCount > 0
                ? `${getTranslation("MANAGE_RETURNS_APPLY_FILTERS", scope)} (${draftCount})`
                : getTranslation("MANAGE_RETURNS_APPLY_FILTERS", scope);
        const canApply =
            draftCount > 0 && rmaListFiltersStore.reserveItemExpirationFilterValid;

        const onApplyFilters = () => {
            const status = rmaListFiltersStore.selectedQuickFilter;
            trackManageReturnsListEvent("d&_apply_filter", MANAGE_RETURNS_DESIGN_VERSION.V2);
            if (status) {
                trackManageReturnsListEvent(`d&_filter_${status}`, MANAGE_RETURNS_DESIGN_VERSION.V2);
            }
            rmaPagedListStore.bulkSelectionStore.clear();
            rmaPagedListStore.loadPage(1);
            onFiltersApplied?.();
        };

        // Buckets apply immediately on change, keeping the (tablet) drawer open so the merchant can
        // add/remove more without it closing. `applyBucketSelection`, not `loadPage`, so a pending
        // sidebar draft is not applied along with it.
        const onApplyBucketSelection = (bucket: RMA_BUCKET_KIND) => {
            trackManageReturnsListEvent(
                BUCKET_APPLY_EVENT[bucket],
                MANAGE_RETURNS_DESIGN_VERSION.V2,
            );
            void rmaPagedListStore.applyBucketSelection();
        };
        
        return (
            <aside className="rma-list-filters-sidebar">
                <div className="rma-list-filters-sidebar__header">
                    <RmaListFiltersSidebarTitle />
                    <RmaListFiltersSidebarClearAll
                        rmaListFiltersStore={rmaListFiltersStore}
                        disabled={disabled}
                        onFiltersApplied={onFiltersApplied}
                    />
                </div>
                <RmaListSelectedFilters
                    rmaListFiltersStore={rmaListFiltersStore}
                    disabled={disabled}
                />
                <RmaListActionsStatusFilter
                    bucketFiltersStore={rmaListFiltersStore.bucketFilters}
                    currentTab={rmaListFiltersStore.currentTab}
                    disabled={disabled}
                    onApply={onApplyBucketSelection}
                />
                <div className="rma-list-filters-sidebar__body">
                    <RmaListQuickFiltersTabs rmaListFiltersStore={rmaListFiltersStore} disabled={disabled} />
                    <RmaListFilterPopoverGroup>
                        <div className="rma-list-filters-sidebar__controls">
                            <RmaListFilterDateRange rmaListFiltersStore={rmaListFiltersStore} disabled={disabled} />
                            <RmaListFilterResolution rmaListFiltersStore={rmaListFiltersStore} disabled={disabled} />
                            <RmaListFilterReturnReason rmaListFiltersStore={rmaListFiltersStore} disabled={disabled} />
                            <RmaListFilterValidated rmaListFiltersStore={rmaListFiltersStore} disabled={disabled} />
                            {isReserveItemsAllowed &&
                                <RmaListFilterReservedItem
                                    rmaListFiltersStore={rmaListFiltersStore}
                                    disabled={disabled}
                                />
                            }
                        </div>
                    </RmaListFilterPopoverGroup>
                </div>
                <div className="rma-list-filters-sidebar__footer">
                    <Button
                        variant={BUTTON_VARIANTS.PrimaryAccent}
                        text={applyLabel}
                        disabled={disabled || !canApply}
                        onClick={onApplyFilters}
                    />
                </div>
            </aside>
        );
    },
);

export default RmaListFiltersSidebar;
